import React, { useContext, useState } from 'react';
import Table from 'antd/lib/table/Table';
import DropOption from '../../components/DropOption/DropOption';
import { EntityType } from '../../integrated-client/models';
import {
  DROP_OPTION_EDIT,
  DROP_OPTION_COPY,
  DROP_OPTION_VIEW,
  DEFAULT_PAGE_SIZE,
  DROP_OPTION_HISTORY,
  DROP_OPTION_DELETE,
  CONFIRM_DELETE_ITEM_TEXT,
  handleDeleteOptionClick,
  handlePageChange,
  handleTableChange,
  SectionKeys,
} from '../../utils/Constants';
import PermissionsChecker, { CATEGORIES } from '../../utils/PermissionsChecker';
import { AuthContext } from '../../auth/AuthContext';
import ModalWindow from '../../components/ModalWindow/ModalWindow';
import HistoryLayout from '../../views/SearchesManager/SearchManagerTable/HistoryLayout/HistoryLayout';
import { ColumnProps } from 'antd/lib/table';
import styles from './TableLayout.module.css';
import '../../views/ShoppingDiagnostics/SDResult/SDPostSearch/SDPostSearchTable/SDPostSearchTable.css';
import Modal from 'antd/lib/modal';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import Card from 'antd/lib/card';
import { DEFAULT_PATHS } from '../../utils/ApiUtils';
import PrimaryButton from '../../components/PrimaryButton/PrimaryButton';
import Row from 'antd/lib/row';
import Col from 'antd/lib/col';
const { confirm } = Modal;

interface ITableLayoutProps {
  id: string; // to be add enum
  history?: any;
  records?: Array<any>; // to be typed
  columns?: Array<ColumnProps<any>>;
  category?: CATEGORIES;
  path: string;
  historyType?: EntityType;
  withDelete?: boolean;
  withCopy?: boolean;
  handlePageChange: handlePageChange;
  handleDeleteOptionClick: handleDeleteOptionClick;
  total: number;
  pageSize?: number;
  currentPage: number;
  addButton: any;
  handleTableChange?: handleTableChange;
  renderOperations?: boolean;
  redirectPath?: string[];
  openOnRowClick?: boolean;
}

const TableLayout: React.FC<ITableLayoutProps> = (props) => {
  const {
    id,
    records,
    currentPage,
    columns,
    category,
    path,
    historyType,
    withDelete = false,
    withCopy = false,
    handlePageChange,
    handleDeleteOptionClick,
    total,
    pageSize,
    history,
    addButton,
    handleTableChange,
    renderOperations = true,
    redirectPath,
    openOnRowClick = false,
  } = props;
  let columnsToRender: Array<ColumnProps<any>> | undefined;

  var localPageSize = pageSize ?? DEFAULT_PAGE_SIZE;

  const authContext: any = useContext(AuthContext);
  const { userPermissions } = authContext;

  const [showRecordId, setShowRecordId] = useState('');

  const pagination = {
    current: currentPage,
    pageSize: localPageSize,
    showSizeChanger: false,
    onChange(page: number) {
      handlePageChange(page);
    },
    total: total,
  };

  const handleGoToLinkWithCopyId = (record: any) => {
    // to be typed
    return history.push(`/${path}/new`, { copyId: record.id });
  };

  const handleGoToEditLink = (record: any) => {
    //to be typed

    if (redirectPath && record?.cardProvider?.label && redirectPath?.includes(record?.cardProvider?.label.split(' ').join('-').toLowerCase())) {
      return history?.push(`/${record?.cardProvider?.label.split(' ').join('-').toLowerCase()}/edit/${record.id}`);
    }
    if (record) return history.push(`/${path}/edit/${record.id}`);
  };

  const closeModal = () => setShowRecordId('');

  const canOpenRow = (record: any) => record?.id != null && PermissionsChecker.isPermitted(category, userPermissions);
  const handleRowOpen = (record: any) => {
    if (canOpenRow(record) && history) handleGoToEditLink(record);
  };

  const handleMenuClick = (record: any, param: any) => {
    // to be typed
    switch (param.key) {
      case DROP_OPTION_EDIT.key:
        history && handleGoToEditLink(record);
        break;
      case DROP_OPTION_COPY.key:
        history && handleGoToLinkWithCopyId(record);
        break;
      case DROP_OPTION_DELETE.key:
        confirm({
          title: CONFIRM_DELETE_ITEM_TEXT,
          icon: <ExclamationCircleOutlined />,
          onOk() {
            if ((record.id || record === 0) && handleDeleteOptionClick) {
              return handleDeleteOptionClick(record.id).catch(() => {});
            }
            return false;
          },
          onCancel() {},
        });
        break;
      case DROP_OPTION_HISTORY.key:
        record.id !== undefined && record.id !== null && setShowRecordId(record.id);
        break;
      default:
        break;
    }
  };

  const getOptions = (record: any) => {
    const isReadOnly = PermissionsChecker.isReadOnly(category, userPermissions) || record.allowEdit === false;
    let optionsToRender = [
      isReadOnly ? DROP_OPTION_VIEW : DROP_OPTION_EDIT,
      !isReadOnly && withCopy ? DROP_OPTION_COPY : null,
      !isReadOnly && withDelete ? DROP_OPTION_DELETE : null,
      DROP_OPTION_HISTORY,
    ];
    return optionsToRender.filter((option) => option !== null);
  };

  const handleAddClick = () => history?.push(`/${DEFAULT_PATHS.PROVIDERS}/new`);

  if (columns) {
    columnsToRender = [
      ...columns,
      {
        title: renderOperations ? 'Operations' : '',
        key: 'operation',
        align: 'center',
        render: (record: any) => {
          return <DropOption onMenuClick={(param) => handleMenuClick(record, param)} menuOptions={getOptions(record)} />;
        },
      },
    ];

    columnsToRender = columnsToRender?.map((column, i) => ({
      ...column,
      width: i === (columnsToRender as Array<ColumnProps<any>>).length - 1 ? '8%' : `${Math.floor(92 / columns.length)}%`,
    }));
  }

  return (
    <>
      <Card title={<strong>Results</strong>} type="inner" className={styles.tableCard}>
        <Table
          id={id}
          bordered
          columns={columnsToRender}
          dataSource={records?.slice(0, localPageSize)}
          pagination={pagination}
          rowKey={(_, index) => `${index}`}
          tableLayout="fixed"
          onChange={handleTableChange}
          onRow={openOnRowClick ? (record: any) => ({
            tabIndex: canOpenRow(record) ? 0 : -1,
            onClick: (event: React.MouseEvent<HTMLElement>) => {
              if (!(event.target as HTMLElement).closest('.ant-dropdown-trigger, button, a, input')) handleRowOpen(record);
            },
            onKeyDown: (event: React.KeyboardEvent<HTMLTableRowElement>) => {
              if (event.target === event.currentTarget && (event.key === 'Enter' || event.key === ' ')) {
                event.preventDefault();
                handleRowOpen(record);
              }
            },
          }) : undefined}
        />
        {addButton && (
          <Row>
            <Col span={'3'}>
              <PrimaryButton block onClick={handleAddClick}>
                Add {DEFAULT_PATHS.PROVIDERS}
              </PrimaryButton>
            </Col>
          </Row>
        )}
      </Card>
      <ModalWindow title={'History'} visible={!!showRecordId} updateState={closeModal} width={'800px'}>
        <HistoryLayout id={showRecordId} type={historyType} />
      </ModalWindow>
    </>
  );
};

export default TableLayout;
